// AGC: Crop Image Cache Service
// AGC: IndexedDB-based offline-first caching for crop background images
// AGC: Supports LOD card premium design with graceful fallbacks

import { openDB, DBSchema, IDBPDatabase } from 'idb'

// AGC: Database configuration
const DB_NAME = 'droidfarm-crop-images'
const DB_VERSION = 1
const STORE_NAME = 'crop_images'

// AGC: Cache TTL - 30 days (images don't change often)
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000

/**
 * AGC: Crop image cache entry
 */
export interface CropImageCacheEntry {
  cropId: string              // AGC: Unique crop identifier
  imageUrl: string            // AGC: Original Firebase Storage URL
  blob: Blob                  // AGC: Cached image data
  cachedAt: string            // AGC: ISO timestamp when cached
  imageUpdatedAt?: string     // AGC: Image version timestamp (for invalidation)
  size: number                // AGC: Blob size in bytes
}

/**
 * AGC: Database schema
 */
interface CropImageCacheDB extends DBSchema {
  crop_images: {
    key: string
    value: CropImageCacheEntry
    indexes: {
      'by-cached-at': string
    }
  }
}

/**
 * AGC: Open IndexedDB database
 */
async function getDB(): Promise<IDBPDatabase<CropImageCacheDB>> {
  return openDB<CropImageCacheDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // AGC: Create crop images store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'cropId' })
        store.createIndex('by-cached-at', 'cachedAt')
      }
    },
  })
}

/**
 * AGC: Check if cache entry is stale
 */
function isCacheStale(cachedAt: string, imageUpdatedAt?: string): boolean {
  const cacheDate = new Date(cachedAt).getTime()
  const now = Date.now()
  
  // AGC: Check TTL expiration
  if (now - cacheDate > CACHE_TTL_MS) {
    return true
  }
  
  // AGC: Check if image was updated after cache
  if (imageUpdatedAt) {
    const updateDate = new Date(imageUpdatedAt).getTime()
    if (updateDate > cacheDate) {
      return true
    }
  }
  
  return false
}

/**
 * AGC: Get cached crop image
 * AGC: Returns blob URL for display, or null if not cached/stale
 * 
 * @param cropId - Crop identifier
 * @param imageUpdatedAt - Optional image version timestamp for validation
 * @returns Blob URL or null
 */
export async function getCachedCropImage(
  cropId: string,
  imageUpdatedAt?: string
): Promise<string | null> {
  try {
    const db = await getDB()
    const entry = await db.get(STORE_NAME, cropId)
    
    if (!entry) {
      console.log(`📦 AGC: No cached image for crop ${cropId}`)
      return null
    }
    
    // AGC: Check if cache is stale
    if (isCacheStale(entry.cachedAt, imageUpdatedAt)) {
      console.log(`⏰ AGC: Cached image for crop ${cropId} is stale, invalidating`)
      await invalidateCropImage(cropId)
      return null
    }
    
    // AGC: Create blob URL from cached data
    const blobUrl = URL.createObjectURL(entry.blob)
    console.log(`✅ AGC: Using cached image for crop ${cropId}`)
    return blobUrl
  } catch (error) {
    console.error(`❌ AGC: Error getting cached image for crop ${cropId}:`, error)
    return null
  }
}

/**
 * AGC: Cache crop image
 * AGC: Downloads image from URL and stores in IndexedDB
 * 
 * @param cropId - Crop identifier
 * @param imageUrl - Firebase Storage URL
 * @param imageUpdatedAt - Optional image version timestamp
 */
export async function cacheCropImage(
  cropId: string,
  imageUrl: string,
  imageUpdatedAt?: string
): Promise<void> {
  try {
    // AGC: Check if online
    if (!navigator.onLine) {
      console.log(`📴 AGC: Offline - cannot cache image for crop ${cropId}`)
      return
    }
    
    console.log(`⬇️ AGC: Downloading image for crop ${cropId}...`)
    
    // AGC: Fetch image
    const response = await fetch(imageUrl)
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`)
    }
    
    const blob = await response.blob()
    
    // AGC: Create cache entry
    const entry: CropImageCacheEntry = {
      cropId,
      imageUrl,
      blob,
      cachedAt: new Date().toISOString(),
      imageUpdatedAt,
      size: blob.size,
    }
    
    // AGC: Store in IndexedDB
    const db = await getDB()
    await db.put(STORE_NAME, entry)
    
    console.log(`✅ AGC: Cached image for crop ${cropId} (${(blob.size / 1024).toFixed(1)} KB)`)
  } catch (error) {
    console.error(`❌ AGC: Error caching image for crop ${cropId}:`, error)
    throw error
  }
}

/**
 * AGC: Get or fetch crop image
 * AGC: Returns cached image if available, otherwise fetches and caches
 * 
 * @param cropId - Crop identifier
 * @param imageUrl - Firebase Storage URL (required if not cached)
 * @param imageUpdatedAt - Optional image version timestamp
 * @returns Blob URL or null
 */
export async function getOrFetchCropImage(
  cropId: string,
  imageUrl?: string,
  imageUpdatedAt?: string
): Promise<string | null> {
  // AGC: Try cache first
  const cachedUrl = await getCachedCropImage(cropId, imageUpdatedAt)
  if (cachedUrl) {
    return cachedUrl
  }
  
  // AGC: If not cached and we have URL, fetch and cache
  if (imageUrl && navigator.onLine) {
    try {
      await cacheCropImage(cropId, imageUrl, imageUpdatedAt)
      return await getCachedCropImage(cropId, imageUpdatedAt)
    } catch (error) {
      console.warn(`⚠️ AGC: Failed to fetch image for crop ${cropId}, using fallback`)
      return null
    }
  }
  
  // AGC: No cache, no URL, or offline
  return null
}

/**
 * AGC: Invalidate (delete) cached crop image
 * 
 * @param cropId - Crop identifier
 */
export async function invalidateCropImage(cropId: string): Promise<void> {
  try {
    const db = await getDB()
    await db.delete(STORE_NAME, cropId)
    console.log(`🗑️ AGC: Invalidated cached image for crop ${cropId}`)
  } catch (error) {
    console.error(`❌ AGC: Error invalidating cached image for crop ${cropId}:`, error)
  }
}

/**
 * AGC: Preload all crop images
 * AGC: Fetches and caches images for all crops with imageUrl
 * AGC: Call this on app startup or after login
 * 
 * @param crops - Array of crop configs with imageUrl
 */
export async function preloadAllCropImages(
  crops: Array<{ id: string; imageUrl?: string; imageUpdatedAt?: string }>
): Promise<void> {
  if (!navigator.onLine) {
    console.log('📴 AGC: Offline - skipping crop image preload')
    return
  }
  
  console.log(`⬇️ AGC: Preloading ${crops.length} crop images...`)
  
  let successCount = 0
  let failCount = 0
  
  // AGC: Fetch in parallel but with some rate limiting
  const batchSize = 3
  for (let i = 0; i < crops.length; i += batchSize) {
    const batch = crops.slice(i, i + batchSize)
    
    await Promise.allSettled(
      batch.map(async (crop) => {
        if (!crop.imageUrl) return
        
        try {
          await cacheCropImage(crop.id, crop.imageUrl, crop.imageUpdatedAt)
          successCount++
        } catch (error) {
          console.warn(`⚠️ AGC: Failed to preload image for crop ${crop.id}`)
          failCount++
        }
      })
    )
  }
  
  console.log(`✅ AGC: Preloaded ${successCount} crop images (${failCount} failed)`)
}

/**
 * AGC: Clear all cached crop images
 * AGC: Useful for debugging or freeing space
 */
export async function clearAllCropImages(): Promise<void> {
  try {
    const db = await getDB()
    await db.clear(STORE_NAME)
    console.log('🗑️ AGC: Cleared all cached crop images')
  } catch (error) {
    console.error('❌ AGC: Error clearing crop images:', error)
  }
}

/**
 * AGC: Get cache statistics
 */
export async function getCacheStats(): Promise<{
  totalImages: number
  totalSize: number
  oldestCache: string | null
  newestCache: string | null
}> {
  try {
    const db = await getDB()
    const entries = await db.getAll(STORE_NAME)
    
    if (entries.length === 0) {
      return { totalImages: 0, totalSize: 0, oldestCache: null, newestCache: null }
    }
    
    const totalSize = entries.reduce((sum, entry) => sum + entry.size, 0)
    const sortedByCached = entries.sort((a, b) => 
      new Date(a.cachedAt).getTime() - new Date(b.cachedAt).getTime()
    )
    
    return {
      totalImages: entries.length,
      totalSize,
      oldestCache: sortedByCached[0].cachedAt,
      newestCache: sortedByCached[sortedByCached.length - 1].cachedAt,
    }
  } catch (error) {
    console.error('❌ AGC: Error getting cache stats:', error)
    return { totalImages: 0, totalSize: 0, oldestCache: null, newestCache: null }
  }
}
